import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Badge, Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { FilterFilled, FilterRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { acquireFilterSurface, releaseFilterSurface } from './filterSurfaceRegistry';
import { isInsideOwnedSurface } from './surfaceOwnership';
import { sameFilterValue } from './filterValueEquality';

// STX-UX-01 — Dataverse-style column-header filtering, now conformant with the fleet filter
// contract §6.6 I1–I5 (binding since v2.0, 2026-10-20), ported from the reference implementation in
// ptc-supervision's `listFilters/ColumnHeaderFilter.tsx` (§14.3: "supervision implements first; each
// app ports"). The mechanics are the reference's; the API stays statetax's, because this repo's
// panels host arbitrary controls (radio lists, checkbox sets, date inputs) rather than a typed
// operator/value pair.
//
// Still a hand-rolled disclosure trigger + absolutely-positioned panel, NOT a Fluent Popover: the
// panel hosts live controls and Popover's portalled surface hangs the DOM test environment.
//
// I1  One surface at a time — `filterSurfaceRegistry`, a page-level slot, because one instance per
//     column cannot see its siblings.
// I2  Escape dismisses and returns focus to the trigger — and under I5 ALSO discards.
// I3  Outside-click dismisses, with "outside" scoped by OWNERSHIP (`surfaceOwnership`): a surface the
//     panel opened — a portalled listbox — is inside wherever it renders. 🔴 This REVERSES the old
//     header comment here ("deliberately NO outside-click dismissal"), which was right about the
//     naive `contains()` dismisser and is now answered by the ownership walk, as the contract asks.
// I4  No overlap-stacking; I1 gives this for filter surfaces by construction.
// I5  🔑 THE PANEL STAGES. Controls edit a local DRAFT; **Apply** is the only commit. Escape, an
//     outside click, the trigger and I1's eviction all close WITHOUT committing.
//
// ⚠ WHAT "ONE FETCH" MEANS HERE. The commit boundary is exactly one `onApply` call — the page then
// sets its filter state in one render, which is one query-key change. The conformance tests count
// `onApply` calls, because "the list updated" is also true under the per-control-change defect.

const useStyles = makeStyles({
  root: {
    position: 'relative',
    display: 'inline-flex',
    // Table header cells are position:relative in Fluent — a LATER sibling th paints over
    // an earlier cell's overflowing content. Lift the trigger (and its panel) above them so
    // the filter stays clickable even when a narrow column lets the trigger jut out.
    zIndex: 1,
  },
  panel: {
    position: 'absolute',
    top: '100%',
    zIndex: 20,
    marginTop: '4px',
    minWidth: '240px',
    maxWidth: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    // Header cells may uppercase/bold their text — filter controls should not inherit that.
    textTransform: 'none',
    letterSpacing: 'normal',
    fontWeight: tokens.fontWeightRegular,
    fontSize: tokens.fontSizeBase300,
    whiteSpace: 'normal',
    textAlign: 'left',
    cursor: 'default',
  },
  alignStart: { left: 0 },
  alignEnd: { right: 0 },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  // Active-value chip: keep header rows tidy — normal casing/weight (header cells may
  // uppercase/bold), single line, and clipped if a caller passes a very long label.
  activeChip: {
    alignSelf: 'center',
    maxWidth: '160px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: 'normal',
  },
});

export interface ColumnHeaderFilterProps<T> {
  /** Already-translated column label — used for the trigger/panel accessible names. */
  columnLabel: string;
  /** The APPLIED value. The funnel, the chip and `active` read this — never the draft (I5). */
  value: T;
  /** The value that means "no filter": what Clear stages. */
  emptyValue: T;
  /** Whether a value is an active filter (filled funnel + chip). */
  isActive: (value: T) => boolean;
  /** The ONLY commit. Called at most once per Apply, and never when the draft equals `value`. */
  onApply: (next: T) => void;
  /** Equality for C37. Defaults to {@link sameFilterValue}. */
  equals?: (a: T, b: T) => boolean;
  /**
   * Already-translated label of the APPLIED value, rendered as a small chip next to the trigger
   * while active. The filled funnel alone proved too subtle for always-on default filters (the
   * Tasks page's "Open" bucket went unnoticed). Omit where the filled icon is enough.
   */
  activeValueLabel?: string;
  /** Panel edge alignment relative to the trigger — use 'end' for right-most columns. */
  align?: 'start' | 'end';
  /**
   * The column's controls, bound to the DRAFT. Nothing they do commits until Apply. `apply` is the panel's own Apply, for a control
   * that commits on Enter (the shared lookup combobox's `onEnter`, C86); it is the same single commit as the button.
   */
  children: (draft: T, setDraft: Dispatch<SetStateAction<T>>, apply: () => void) => ReactNode;
}

export default function ColumnHeaderFilter<T>({
  columnLabel,
  value,
  emptyValue,
  isActive,
  onApply,
  equals = sameFilterValue,
  activeValueLabel,
  align = 'start',
  children,
}: ColumnHeaderFilterProps<T>) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // I5: the DRAFT, reseeded from the applied value on EVERY open — which is what makes re-open show
  // last-applied rather than abandoned edits (C36's re-open clause).
  const [draft, setDraft] = useState<T>(value);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Stable identity: the registry holds this and calls it to evict us (I1).
  const close = useCallback(() => setOpen(false), []);

  // Our own dismissal gives up the slot; eviction by the registry clears it before calling us.
  const dismiss = useCallback(() => {
    close();
    releaseFilterSurface(close);
  }, [close]);

  useEffect(() => () => releaseFilterSurface(close), [close]);

  // I3 — dismissal scoped by ownership, not containment.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (isInsideOwnedSurface(e.target as Node | null, [triggerRef.current, panelRef.current])) return;
      dismiss();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, dismiss]);

  const active = isActive(value);

  function openPanel() {
    setDraft(value);
    setOpen(true);
    acquireFilterSurface(close);
  }

  function dismissAndRefocus() {
    dismiss();
    triggerRef.current?.focus();
  }

  function apply() {
    // C37: Apply with nothing staged is not a commit — an unconditional emit passes C35 and
    // quietly doubles the traffic of every popover visit.
    if (!equals(draft, value)) onApply(draft);
    dismissAndRefocus();
  }

  return (
    <span
      className={styles.root}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          // I2 + I5: dismiss AND discard. The draft is reseeded on the next open.
          e.stopPropagation();
          dismissAndRefocus();
        }
      }}
    >
      {active && activeValueLabel ? (
        <Badge appearance="tint" color="brand" size="small" className={styles.activeChip}>
          {activeValueLabel}
        </Badge>
      ) : null}
      <Button
        ref={triggerRef}
        appearance="subtle"
        size="small"
        icon={active ? <FilterFilled /> : <FilterRegular />}
        aria-label={t('common.columnFilter.trigger', { column: columnLabel })}
        aria-pressed={active}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => (open ? dismissAndRefocus() : openPanel())}
      />
      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          role="group"
          aria-label={t('common.columnFilter.panel', { column: columnLabel })}
          className={mergeClasses(styles.panel, align === 'end' ? styles.alignEnd : styles.alignStart)}
          // Header cells are often wrapped in sort buttons / clickable rows — keep panel
          // interactions from bubbling into them.
          onClick={(e) => e.stopPropagation()}
        >
          {children(draft, setDraft, apply)}
          {/* C67: every filter surface carries BOTH, always — the presence is the contract. */}
          <div className={styles.actions}>
            <Button
              size="small"
              appearance="subtle"
              // I5 lean (contract §6.6, flagged as a lean): Clear is an EDIT, so it stages.
              // Clear-then-Escape leaves the filter applied; Clear-then-Apply removes it (C38).
              onClick={() => setDraft(emptyValue)}
            >
              {t('common.columnFilter.clear')}
            </Button>
            <Button size="small" appearance="primary" onClick={apply}>
              {t('common.columnFilter.apply')}
            </Button>
          </div>
        </div>
      ) : null}
    </span>
  );
}
